import { AlertTriangle, ArrowLeft, CheckCircle, Link03, Loading01 } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { useAdsAuthorisation, useSelectAdsAdvertiser } from "@/api/ads-authorisation";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Button } from "@/components/base/buttons/button";
import { cx } from "@/utils/cx";
import { AdvertiserPicker, buildAdvertiserGroups } from "./advertiser-picker";
import {
  CONSENT_SCREENSHOT_ALT,
  CONSENT_SCREENSHOT_SRC,
  ConnectAdsShell,
  WhyUnboxNeedsAccess,
} from "./connect-ads-shell";

const apiUrl = import.meta.env.VITE_API_URL;

const HOLD_SECONDS = 5;
const HOLD_STEPS = ["Authorizing with TikTok", "Validating permissions"];

const ScopeCheckFailed = ({ reauthorizeUrl }: { reauthorizeUrl: string }) => (
  <div className="mx-auto max-w-[560px]">
    <span aria-hidden className="inline-flex text-fg-warning-primary">
      <AlertTriangle className="h-7 w-7" />
    </span>
    <h1 className="mt-2 text-xl font-medium text-primary">You did not authorize the correct fields</h1>
    <p className="mt-1 text-sm text-tertiary">
      Every field under &ldquo;Access Your Ad Account For&rdquo; needs to be ticked. Without them we can&rsquo;t read
      your ad performance or manage which videos run in your campaigns.
    </p>

    <div className="mt-5 overflow-hidden rounded-xl border border-secondary">
      <img src={CONSENT_SCREENSHOT_SRC} alt={CONSENT_SCREENSHOT_ALT} className="h-[200px] w-full object-cover" />
    </div>

    <WhyUnboxNeedsAccess />

    <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
      <Button size="lg" iconLeading={Link03} href={reauthorizeUrl}>
        Edit Authorization
      </Button>
      <Button size="lg" isDisabled>
        Confirm and authorize Unbox
      </Button>
    </div>
  </div>
);

const ConnectingHold = ({ isReady, onDone }: { isReady: boolean; onDone: () => void }) => {
  const [remaining, setRemaining] = useState(HOLD_SECONDS);

  useEffect(() => {
    if (remaining <= 0) return;
    const timeout = setTimeout(() => setRemaining((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timeout);
  }, [remaining]);

  useEffect(() => {
    if (remaining <= 0 && isReady) onDone();
  }, [remaining, isReady, onDone]);

  const elapsed = HOLD_SECONDS - remaining;
  const activeStep = Math.min(Math.floor(elapsed / (HOLD_SECONDS / HOLD_STEPS.length)), HOLD_STEPS.length - 1);

  return (
    <div className="mx-auto max-w-[520px] text-center">
      <LoadingIndicator type="line-simple" size="md" />
      <h1 className="mt-4 text-display-xs font-medium text-primary">Connecting your account</h1>
      <p className="mt-1.5 text-sm text-tertiary">
        We are validating the access. You will be taken back to Unbox automatically.
      </p>

      <ul aria-live="polite" className="mx-auto mt-8 flex max-w-[320px] list-none flex-col gap-3 p-0 text-left">
        {HOLD_STEPS.map((step, index) => {
          const done = index < activeStep;
          return (
            <li key={step} className="flex items-center gap-2.5">
              <span
                aria-hidden
                className={done ? "inline-flex text-fg-success-primary" : "inline-flex text-brand-secondary"}
              >
                {done ? (
                  <CheckCircle className="h-[18px] w-[18px]" />
                ) : (
                  <Loading01 className="h-[18px] w-[18px] animate-spin" />
                )}
              </span>
              <span className={cx("text-sm", index <= activeStep ? "font-medium text-primary" : "text-quaternary")}>
                {step}
              </span>
            </li>
          );
        })}
      </ul>

      <p className="mt-8 text-sm text-tertiary">
        Redirecting in {remaining}s. Not working?{" "}
        <button
          type="button"
          onClick={onDone}
          className="cursor-pointer font-semibold text-brand-secondary underline underline-offset-2"
        >
          Click to go back to Unbox.
        </button>
      </p>
    </div>
  );
};

export const AdsAuthorisationPage = ({ authorisationId }: { authorisationId: string }) => {
  const { data: authorisation, isLoading, error } = useAdsAuthorisation(authorisationId);
  const select = useSelectAdsAdvertiser(authorisationId);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectError, setSelectError] = useState<string | undefined>();
  const [isHolding, setIsHolding] = useState(false);
  const [appUrl, setAppUrl] = useState<string | null>(null);

  const reauthorizeUrl = `${apiUrl}/ads-auth/${authorisationId}/reauthorize`;

  const groups = useMemo(
    () =>
      authorisation
        ? buildAdvertiserGroups(
            authorisation.advertisers,
            authorisation.merchantName,
            authorisation.merchantRegion ?? null,
          )
        : [],
    [authorisation],
  );

  const bestMatchId = useMemo(
    () => groups.flatMap((group) => group.options).find((option) => option.isBestMatch)?.advertiserId ?? null,
    [groups],
  );
  const defaultId = bestMatchId ?? groups[0]?.options[0]?.advertiserId ?? null;
  const advertiserId = selectedId ?? defaultId;

  const returnToApp = () => {
    window.location.href = `${appUrl ?? "https://app.unbox.social"}/settings/integrations?ads=connected`;
  };

  const handleSelect = async () => {
    if (!advertiserId) return;
    setIsHolding(true);
    try {
      const result = await select.mutateAsync(advertiserId);
      setAppUrl(result.appUrl);
    } catch {
      setIsHolding(false);
      setSelectError("We could not save that account. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <ConnectAdsShell>
        <div className="flex justify-center py-10">
          <LoadingIndicator type="line-simple" size="md" />
        </div>
      </ConnectAdsShell>
    );
  }

  if (error || !authorisation) {
    return (
      <ConnectAdsShell>
        <div className="mx-auto max-w-[560px] text-center">
          <h1 className="text-xl font-medium text-primary">This authorization link has expired</h1>
          <p className="mt-1 text-sm text-tertiary">
            Please start again from Unbox, or ask your Unbox contact for a new link.
          </p>
        </div>
      </ConnectAdsShell>
    );
  }

  if (isHolding) {
    return (
      <ConnectAdsShell>
        <ConnectingHold isReady={!!appUrl} onDone={returnToApp} />
      </ConnectAdsShell>
    );
  }

  if (authorisation.missingScopes.length > 0) {
    return (
      <ConnectAdsShell>
        <ScopeCheckFailed reauthorizeUrl={reauthorizeUrl} />
      </ConnectAdsShell>
    );
  }

  return (
    <ConnectAdsShell>
      <div className="mx-auto max-w-[560px]">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-fg-success-primary">
          <CheckCircle className="h-4 w-4" aria-hidden />
          Authorised on TikTok
        </span>
        <h1 className="mt-2 text-xl font-medium text-primary">Confirm your advertiser account</h1>
        <p className="mt-1 text-sm text-tertiary">
          We've picked the closest match to your shop. Only the account you confirm is saved. We ignore the rest.
        </p>

        <AdvertiserPicker
          groups={groups}
          selectedId={advertiserId}
          onSelect={(advertiserId) => {
            setSelectedId(advertiserId);
            setSelectError(undefined);
          }}
        />

        {selectError && (
          <p role="alert" className="mt-3 text-sm text-error-primary">
            {selectError}
          </p>
        )}

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <Button color="secondary" size="lg" iconLeading={ArrowLeft} href={reauthorizeUrl}>
            Back
          </Button>
          <Button size="lg" onClick={handleSelect} isLoading={select.isPending} isDisabled={!advertiserId}>
            Confirm and authorize Unbox
          </Button>
        </div>
      </div>
    </ConnectAdsShell>
  );
};
